import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, HeartHandshake, Sparkles, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  helpOptions, contributionOptions, sessionOptions,
  formatOptions, audienceOptions, legalDisclaimer, media
} from '../siteContent';
import { useSiteContent } from '../context/SiteContentContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chip = ({ label, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`rounded-full px-4 py-2 text-sm font-medium border transition-all duration-200 ${
      active
        ? 'bg-[#E74C3C] border-[#E74C3C] text-white'
        : 'bg-white border-[#EAE5E0] text-[#1A1A1A] hover:border-[#E74C3C]'
    }`}
  >
    {label}
  </button>
);

const Membership = () => {
  const { membership: cm } = useSiteContent();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', industry: '', company: '',
    designation: '', years_experience: '', linkedin: '', city: '', password: ''
  });
  const [helpAreas, setHelpAreas] = useState([]);
  const [contribution, setContribution] = useState([]);
  const [sessions, setSessions] = useState('');
  const [pFormat, setPFormat] = useState('');
  const [audience, setAudience] = useState([]);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/members/`, {
        ...form,
        membership_plan: 'founding_member',
        help_areas: helpAreas,
        contribution_areas: contribution,
        sessions_per_year: sessions,
        preferred_format: pFormat,
        preferred_audience: audience
      });
      // Auto-login: store token returned by the signup endpoint
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user || { email: form.email, full_name: form.full_name, role: 'member' }));
      }
      toast({ title: 'Welcome to Equaltoo!', description: 'Your Founding Membership is confirmed. You now have full member access.' });
      setForm({ full_name: '', email: '', phone: '', industry: '', company: '', designation: '', years_experience: '', linkedin: '', city: '', password: '' });
      setHelpAreas([]); setContribution([]); setSessions(''); setPFormat(''); setAudience([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FDFBF9]">
      {/* Hero */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EAE5E0] px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#003366] mb-6">
            <Sparkles className="w-4 h-4 text-[#E74C3C]" /> {cm.hero.badge}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A]">
            {cm.hero.heading} <span className="text-[#E74C3C]">{cm.hero.headingHighlight}</span>
          </h1>
          <p className="mt-6 text-lg text-[#666666] leading-relaxed">
            {cm.hero.subtext}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Personal & Professional */}
            <div className="bg-white rounded-3xl border border-[#EAE5E0] p-8 lg:p-10">
              <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-6">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input id="full_name" name="full_name" value={form.full_name} onChange={handleChange} required className="mt-2 rounded-xl" data-testid="input-fullname" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required className="mt-2 rounded-xl" data-testid="input-email" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" name="phone" value={form.phone} onChange={handleChange} className="mt-2 rounded-xl" data-testid="input-phone" />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" name="city" value={form.city} onChange={handleChange} className="mt-2 rounded-xl" data-testid="input-city" />
                </div>
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input id="industry" name="industry" value={form.industry} onChange={handleChange} className="mt-2 rounded-xl" data-testid="input-industry" />
                </div>
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" name="company" value={form.company} onChange={handleChange} className="mt-2 rounded-xl" data-testid="input-company" />
                </div>
                <div>
                  <Label htmlFor="designation">Designation</Label>
                  <Input id="designation" name="designation" value={form.designation} onChange={handleChange} className="mt-2 rounded-xl" data-testid="input-designation" />
                </div>
                <div>
                  <Label htmlFor="years_experience">Years of Experience</Label>
                  <Input id="years_experience" name="years_experience" value={form.years_experience} onChange={handleChange} className="mt-2 rounded-xl" data-testid="input-experience" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input id="linkedin" name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/..." className="mt-2 rounded-xl" data-testid="input-linkedin" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="password">Create a Password *</Label>
                  <Input id="password" name="password" type="password" value={form.password} onChange={handleChange} required minLength={6} placeholder="At least 6 characters — for your member portal login" className="mt-2 rounded-xl" data-testid="input-password" />
                  <p className="text-xs text-[#666666] mt-1.5">You'll use this with your email to log in and request bookings.</p>
                </div>
              </div>

              <div className="mt-8">
                <Label className="block mb-3">How can Equaltoo help you?</Label>
                <div className="flex flex-wrap gap-2.5">
                  {helpOptions.map((h) => (
                    <Chip key={h} label={h} active={helpAreas.includes(h)} onClick={() => toggle(helpAreas, setHelpAreas, h)} testid={`help-${h.toLowerCase()}`} />
                  ))}
                </div>
              </div>
            </div>

            {/* Contribution — mandatory feel */}
            <div className="bg-[#003366] text-white rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-3">
                <HeartHandshake className="w-6 h-6 text-[#E74C3C]" />
                <span className="text-sm uppercase tracking-[0.2em] font-semibold text-white/60">{cm.contributionSection.eyebrow}</span>
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">{cm.contributionSection.heading}</h2>
              <p className="text-white/70 text-sm mb-6">{cm.contributionSection.subtext}</p>
              <div className="flex flex-wrap gap-2.5">
                {contributionOptions.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggle(contribution, setContribution, c)}
                    data-testid={`contrib-${c.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`rounded-full px-4 py-2 text-sm font-medium border transition-all duration-200 ${
                      contribution.includes(c)
                        ? 'bg-[#E74C3C] border-[#E74C3C] text-white'
                        : 'bg-white/10 border-white/20 text-white hover:border-white/60'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-8 mt-10">
                <div>
                  <Label className="block mb-3 text-white/80">How many sessions can you volunteer each year?</Label>
                  <div className="flex flex-wrap gap-2.5">
                    {sessionOptions.map((s) => (
                      <button key={s} type="button" onClick={() => setSessions(s)} data-testid={`sessions-${s.toLowerCase()}`}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${sessions === s ? 'bg-[#E74C3C] border-[#E74C3C]' : 'bg-white/10 border-white/20 hover:border-white/60'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="block mb-3 text-white/80">Preferred Format</Label>
                  <div className="flex flex-wrap gap-2.5">
                    {formatOptions.map((f) => (
                      <button key={f} type="button" onClick={() => setPFormat(f)} data-testid={`format-${f.toLowerCase()}`}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${pFormat === f ? 'bg-[#E74C3C] border-[#E74C3C]' : 'bg-white/10 border-white/20 hover:border-white/60'}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Label className="block mb-3 text-white/80">Preferred Audience</Label>
                <div className="flex flex-wrap gap-2.5">
                  {audienceOptions.map((a) => (
                    <button key={a} type="button" onClick={() => toggle(audience, setAudience, a)} data-testid={`audience-${a.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${audience.includes(a) ? 'bg-[#E74C3C] border-[#E74C3C]' : 'bg-white/10 border-white/20 hover:border-white/60'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button type="submit" disabled={submitting} data-testid="membership-submit-btn"
              className="w-full rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white py-6 text-base font-semibold group">
              {submitting ? 'Submitting...' : 'Join the Founding Community'}
              {!submitting && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
            </Button>

            <p className="text-xs text-[#666666] leading-relaxed text-center">{legalDisclaimer}</p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Membership;
